import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { LoaderService } from '../../shared/services/loader.service';
import { NotificationService } from '../../shared/services/notification.service';
import { environment } from '../../../environment/environment';
import { applyImageFallback } from '../../shared/utils/image-fallback';

@Component({
  selector: 'app-category-cards',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './category-cards.component.html',
  styleUrl: './category-cards.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryCardsComponent implements OnInit, OnDestroy {
  scrapList: any[] = [];
  usedList: any[] = [];
  categories: any[] = [];
  selectedCategory: any = 'scrap'; ///'used' | 'scrap' = 'used';
  isModalOpen = false;
  selectedTab: any = 'scrap';
  selectedUsedPart: any = null;
  environment1 = environment.s3BucketUrl;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private apiService: ApiService,
    private behaviourSubject: BehaviourSubjectService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadScrapAndUsedParts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCategorySelect(category: 'used' | 'scrap'): void {
    this.selectedCategory = category;
    this.loadScrapAndUsedParts();
  }

  loadScrapAndUsedParts(): void {
    this.loaderService.show();

    try {
      const payload = {};

      this.apiService
        .get_category(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (value) => {
            if (value.success && value.data) {
              this.scrapList = value.data.scrap_list || [];
              this.usedList = value.data.used_list || [];
              this.categories = [...this.scrapList, ...this.usedList];
              this.cdr.markForCheck();
            } else {
              console.warn('Category API returned success: false');
              this.scrapList = [];
              this.usedList = [];
              this.categories = [];
              this.notificationService.info(
                'No categories available at the moment',
              );
              this.cdr.markForCheck();
            }
            this.loaderService.hide();
          },
          error: (err) => {
            console.error('Error loading category list:', err);
            this.scrapList = [];
            this.usedList = [];
            this.categories = [];
            this.loaderService.hide();

            // User-friendly error notifications
            // if (err.status === 404) {
            //   this.notificationService.info('No categories found');
            // } else if (err.status === 500) {
            //   this.notificationService.error(
            //     'Server error. Please try again later.',
            //   );
            // } else if (err.status === 401) {
            //   this.notificationService.error(
            //     'Authentication required. Please log in.',
            //   );
            // } else if (err.status === 403) {
            //   this.notificationService.error(
            //     'Access denied. You do not have permission to view categories.',
            //   );
            // } else {
            //   this.notificationService.error(
            //     err?.error?.data?.msg ||
            //       'Failed to load categories. Please try again.',
            //   );
            // }
            this.cdr.markForCheck();
          },
          complete: () => {
            this.loaderService.hide();
          },
        });
    } catch (error) {
      console.error('Error in category list:', error);
      this.scrapList = [];
      this.usedList = [];
      this.categories = [];
      this.loaderService.hide();
      // this.notificationService.error(
      //   'An unexpected error occurred while loading categories',
      // );
      this.cdr.markForCheck();
    }
  }

  selectTab(tab: 'used' | 'scrap'): void {
    this.selectedCategory = tab;
    this.cdr.markForCheck();
  }

  gotoProductlist(data: any, type?: any): void {
    // Clear all relevant localStorage items
    const itemsToRemove = [
      'guideSearchData',
      'seemore',
      'category',
      'categoryType',
      'filterState',
      'mostSearchedModel',
      'MyGarageData',
      'SpareCategory',
    ];

    itemsToRemove.forEach((item) => localStorage.removeItem(item));

    // Set new category data
    localStorage.setItem('categoryType', JSON.stringify(type));
    localStorage.setItem('category', JSON.stringify(data));

    // Clear search text and filters when switching categories/part types
    this.behaviourSubject.setSearchTerm('');
    this.behaviourSubject.clearSelectedFilters();

    this.selectedUsedPart = { ...data };

    // Navigate to product list
    this.router.navigate(['/product-list']);
  }

  openModal(tab: 'usedParts' | 'scrap'): void {
    this.isModalOpen = false;
    this.selectedTab = tab;
    this.isModalOpen = true;
    this.cdr.markForCheck();
  }

  /**
   * Handles image load errors by setting a fallback image
   */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img || img.dataset['fallbackApplied']) {
      applyImageFallback(event);
      return;
    }

    img.dataset['fallbackApplied'] = 'true';
    img.src =
      this.environment1 +
      '/bookonline-parts-tatamotors-com-circularity/circularity/circularity/Category%20Part/8330.webp';
  }

  /**
   * TrackBy function for category lists to improve rendering performance
   */
  trackByCategory(index: number, category: any): any {
    return category.id || category.name || index;
  }
}

export interface Category {
  id: string;
  name: string;
  imageUrl: string;
  altText: string;
  type: 'used' | 'scrap';
}
